import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ViewMode } from "@coso/forms";
import { appendRowNodeIdToInputAriaLabel, suppressTabbingToNextCell, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { ColDef, GridOptions, ICellRendererParams, RowNode, SelectionChangedEvent } from "ag-grid-community";
import * as lodash from "lodash";
import { forkJoin } from "rxjs";
import {
  AccessRights,
  GroupStorage,
  HighAvailabilityModes,
  IConfigurationHolder,
  IEntityInfo,
  IPermissionMappingItem,
  IPermissionPairHolder,
  IPermissionPreview,
  MappingMode
} from "../../../../models/auto-generated";
import { PermissionMappingDto } from "../../../../models/permission-mapping-dto";
import { AUserPermissionsService } from "../../../../services/a-user-permissions.service";
import { EntitiesService } from "../../../../services/entities.service";
import { PermissionMappingService } from "../../../../services/permission-mapping.service";
import { ReplicationService } from "../../../../services/replication.service";
import { SettingsService } from "../../../../services/settings.service";
import { handleError } from "../../../../utils/handle-error";
import { isRunning } from "../../../../utils/is-running";
import { AddEditPermissionMappingComponent } from "./add-edit-permission-mapping/add-edit-permission-mapping.component";
import { PermissionMappingSettingsComponent } from "./permission-mapping-settings/permission-mapping-settings.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./permission-mapping.component.html",
  styleUrls: ["./permission-mapping.component.scss"]
})
export class PermissionMappingComponent implements OnInit {
  @ViewChild("actionButtons", { static: true }) actionButtons: TemplateRef<any>;

  /** Indicates whether initialization failed. If true, page will be non-interactive. */
  initFailed: boolean;

  instanceId: string;
  sharePointId: number;
  spConfigIndex: number;
  isReadOnly: boolean;
  isRunning: boolean;
  isFailoverBackupInstance: boolean;
  settings: IConfigurationHolder;
  entities: IEntityInfo[];
  permissionPreview: IPermissionPreview;
  permissionPairs: IPermissionPairHolder[];

  errorMessage: string;
  successMessage: string;

  gridOptions: GridOptions;
  columnDefs: ColDef[];
  rowData: PermissionMappingDto[] = [];
  selectedRows: PermissionMappingDto[] = [];

  addEditModalRef: NgbModalRef;
  settingsModalRef: NgbModalRef;

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private replicationService: ReplicationService,
    private userPermissionsService: AUserPermissionsService,
    private settingsService: SettingsService,
    private permissionMappingService: PermissionMappingService,
    private entitiesService: EntitiesService
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.params["instance-id"];
    this.sharePointId = this.route.snapshot.params["sharepoint-id"];
    this.gridOptions = {
      rowHeight: 40,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      isRowSelectable: this.isRowSelectable.bind(this),
      onSelectionChanged: this.onSelectionChanged.bind(this),
      defaultColDef: { sortable: true, resizable: true, minWidth: 100, suppressKeyboardEvent: suppressTabbingToNextCell },
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of Permission Mappings");
        e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
      }
    };
    this.columnDefs = [
      {
        headerName: "Custom",
        field: "isCustom",
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        sortable: false,
        cellRenderer: (params) => (params.value ? "Yes" : "No")
      },
      { headerName: "Dynamics 365 Access Right", field: "name" },
      {
        headerName: "SharePoint Permission Level",
        field: "privilegeLevel.name",
        cellRenderer: (params) => (params.value ? params.value : "---")
      },
      {
        headerName: "Entity Filter",
        field: "entityName",
        cellRenderer: (params) => (params.value ? params.value : "---")
      },
      {
        headerName: "Actions",
        minWidth: 80,
        maxWidth: 80,
        sortable: false,
        resizable: false,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.actionButtons }
      }
    ];
    this.loadSettings();
    this.loadPermissionMappings(() => (this.initFailed = true));
  }

  openSettingsModal() {
    this.successMessage = undefined;
    this.settingsModalRef = this.modalService.open(PermissionMappingSettingsComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-xs"
    });
    this.settingsModalRef.componentInstance.isReadOnly = this.isReadOnly;
    this.settingsModalRef.componentInstance.mappingMode = this.settings.sharePoint.sharepointConfig[this.spConfigIndex].permissionMapping.mode;
    this.settingsModalRef.componentInstance.saveCallback = this.saveSettings.bind(this);
  }

  openCreateModal() {
    this.successMessage = undefined;
    this.addEditModalRef = this.modalService.open(AddEditPermissionMappingComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: this.settings.groupStorage === GroupStorage.SharePoint ? "modal-lg" : "modal-xl"
    });
    this.addEditModalRef.componentInstance.viewMode = ViewMode.Create;
    this.addEditModalRef.componentInstance.groupStorage = this.settings.groupStorage;
    this.addEditModalRef.componentInstance.entities = this.entities;
    this.addEditModalRef.componentInstance.existingPrivilegesLevels = this.permissionPreview.existingPrivilegesLevels;
    this.addEditModalRef.componentInstance.saveCallback = this.upsertPermissionMapping.bind(this);
  }

  openEditModal(params: ICellRendererParams) {
    this.successMessage = undefined;

    this.addEditModalRef = this.modalService.open(AddEditPermissionMappingComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: this.settings.groupStorage === GroupStorage.SharePoint ? "modal-lg" : "modal-xl"
    });
    this.addEditModalRef.componentInstance.viewMode = ViewMode.Edit;
    this.addEditModalRef.componentInstance.groupStorage = this.settings.groupStorage;
    this.addEditModalRef.componentInstance.entities = this.entities;
    this.addEditModalRef.componentInstance.permissionMappingToEdit = params.data;
    this.addEditModalRef.componentInstance.existingPrivilegesLevels = this.permissionPreview.existingPrivilegesLevels;
    this.addEditModalRef.componentInstance.saveCallback = (holder: IPermissionPairHolder) => this.upsertPermissionMapping(holder, params.data);
  }

  private upsertPermissionMapping(newPair: IPermissionPairHolder, existingPair?: PermissionMappingDto) {
    this.addEditModalRef.componentInstance.errorMessage = undefined;
    const preview = lodash.cloneDeep(this.permissionPreview);
    if (existingPair) {
      this.removeMappingIfMatch(preview, existingPair);
      this.removePairIfMatch(existingPair);
    }
    const accessRightName = AccessRights[newPair.crM_AccessRight];
    const currentMapping = preview.currentMapping[accessRightName];
    if (currentMapping) {
      const index = currentMapping.findIndex(
        (item: IPermissionMappingItem) =>
          // eslint-disable-next-line eqeqeq
          item.accessRight == newPair.crM_AccessRight &&
          // eslint-disable-next-line eqeqeq
          item.entityName == newPair.entityName &&
          item.privilegeLevel &&
          // eslint-disable-next-line eqeqeq
          item.privilegeLevel.name == newPair.sharePoint_PrivilegeLevelName
      );

      if (index > -1) {
        this.addEditModalRef.componentInstance.errorMessage = `Permission mapping from "${accessRightName}" to "${newPair.sharePoint_PrivilegeLevelName}" for entity "${newPair.entityName}" already exists.`;
        return;
      }
    }
    this.permissionPairs.push(newPair);
    this.updatePermissionPairs(
      () => {
        this.addEditModalRef.close();
        this.successMessage = `Permission mapping has been successfully saved.`;
      },
      (err) => (this.addEditModalRef.componentInstance.errorMessage = `Failed to save permission mapping due to: ${handleError(err)}`)
    );
  }

  removeSelected() {
    this.errorMessage = this.successMessage = undefined;
    this.selectedRows.forEach((r) => this.removePairIfMatch(r));
    this.updatePermissionPairs(
      () => (this.successMessage = `Permission mapping has been successfully saved.`),
      (error) => (this.errorMessage = `Failed to delete permission mapping due to: ${handleError(error)}`)
    );
  }

  private isRowSelectable(event: RowNode) {
    return this.isReadOnly === false && event.data.isCustom;
  }

  private onSelectionChanged(event: SelectionChangedEvent) {
    event.api.forEachNode(appendRowNodeIdToInputAriaLabel);
    const rows = event.api.getSelectedRows();
    this.selectedRows = Object.assign([], rows);
  }

  private saveSettings(mode: MappingMode) {
    const newSettings = Object.assign({}, this.settings);
    newSettings.sharePoint.sharepointConfig[this.spConfigIndex].permissionMapping.mode = mode;
    this.updateSettings(
      newSettings,
      () => {
        this.settingsModalRef.close();
        this.successMessage = `Settings have been successfully saved.`;
      },
      (error) => (this.settingsModalRef.componentInstance.errorMessage = `Failed to save settings due to: ${handleError(error)}`)
    );
  }

  private updatePermissionPairs(successCallback: () => void = null, errorCallback: (error: any) => void = null) {
    this.successMessage = this.errorMessage = undefined;
    const settings = Object.assign({}, this.settings);
    settings.sharePoint.sharepointConfig[this.spConfigIndex].permissionMapping.permissionPairs = this.permissionPairs;
    this.updateSettings(settings, successCallback, errorCallback);
  }

  private updateSettings(newValue: IConfigurationHolder, successCallback: () => void = null, errorCallback: (error: any) => void = null) {
    this.loadingService.start("Saving permission mapping", "saveSettings");
    this.settingsService.save(this.instanceId, newValue).subscribe(
      (data) => {
        console.log("updateSettings", data);
        this.settings = data;
        if (successCallback) successCallback();
        this.loadPermissionMappings();
        this.loadingService.stop("saveSettings");
      },
      (error) => {
        if (errorCallback) errorCallback(error);
        this.loadingService.stop("saveSettings");
      }
    );
  }

  private loadPermissionMappings(errorCallback: (error: any) => void = null) {
    this.loadingService.start("Loading permission mapping", "loadPermissionMappings");
    forkJoin([this.permissionMappingService.get(this.instanceId, this.sharePointId), this.entitiesService.get(this.instanceId)]).subscribe(
      ([permissions, entities]) => {
        this.rowData = [];
        this.permissionPreview = permissions;
        this.permissionPreview.existingPrivilegesLevels.unshift({
          name: "---NONE---",
          id: null,
          customPrivileges: undefined,
          default_CRM_AccessRight: undefined
        });
        this.permissionPairs = Object.assign([], this.settings.sharePoint.sharepointConfig[this.spConfigIndex].permissionMapping.permissionPairs);
        const keys = Object.keys(permissions.currentMapping);
        for (let i = 0; i < keys.length; i++) {
          for (let j = 0; j < permissions.currentMapping[keys[i]].length; j++) {
            const permissionObject: PermissionMappingDto = {
              name: keys[i],
              accessRight: permissions.currentMapping[keys[i]][j].accessRight,
              entityName: permissions.currentMapping[keys[i]][j].entityName,
              isCustom: permissions.currentMapping[keys[i]][j].isCustom,
              privilegeLevel: permissions.currentMapping[keys[i]][j].privilegeLevel
            };
            this.rowData.push(permissionObject);
          }
        }
        const indexOfEverything = this.rowData.findIndex((element) => element.name.indexOf(",") > -1);
        if (indexOfEverything > -1) {
          this.rowData[indexOfEverything].name = "Everything";
        }

        this.entities = entities;
        this.entities.unshift({
          displayName: "---NONE---",
          logicalName: null,
          isActivity: false
        });
        this.loadingService.stop("loadPermissionMappings");
      },
      (error) => {
        if (errorCallback) {
          errorCallback(error);
        }
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadPermissionMappings");
      }
    );
  }

  private removeMappingIfMatch(preview: IPermissionPreview, dto: PermissionMappingDto) {
    const accessRightName = AccessRights[dto.accessRight];
    const currentMapping = preview.currentMapping[accessRightName];
    if (currentMapping) {
      const index = currentMapping.findIndex(
        (item: IPermissionMappingItem) =>
          // eslint-disable-next-line eqeqeq
          item.accessRight == dto.accessRight &&
          // eslint-disable-next-line eqeqeq
          item.entityName == dto.entityName &&
          lodash.isEqual(item.privilegeLevel, dto.privilegeLevel)
      );
      if (index > -1) {
        currentMapping.splice(index, 1);
      }
    }
  }

  private removePairIfMatch(dto: PermissionMappingDto) {
    const index = this.permissionPairs.findIndex((pair) => {
      // eslint-disable-next-line eqeqeq
      if (pair.crM_AccessRight != dto.accessRight || pair.entityName != dto.entityName) {
        return false;
      }
      const hasNoPrivilegeLevel = (!pair.sharePoint_RoleID || !pair.sharePoint_PrivilegeLevelName) && !dto.privilegeLevel;
      // eslint-disable-next-line eqeqeq
      const doPrivilegeLevelIdsMatch = dto.privilegeLevel && pair.sharePoint_RoleID && pair.sharePoint_RoleID == dto.privilegeLevel.id;
      // eslint-disable-next-line eqeqeq
      const doPrivilegeLevelNamesMatch = dto.privilegeLevel && pair.sharePoint_PrivilegeLevelName && pair.sharePoint_PrivilegeLevelName == dto.privilegeLevel.name;
      return hasNoPrivilegeLevel || doPrivilegeLevelIdsMatch || doPrivilegeLevelNamesMatch;
    });
    if (index > -1) {
      this.permissionPairs.splice(index, 1);
    }
  }

  private loadSettings() {
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.settingsService.get(this.instanceId)]).subscribe(
      ([status, hasLimitedAccess, currentSettings]) => {
        this.isReadOnly = hasLimitedAccess;
        this.isRunning = isRunning(status);
        if (this.isSaaS === false) {
          this.isFailoverBackupInstance = currentSettings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.isReadOnly = this.isReadOnly || this.isFailoverBackupInstance || this.isRunning;
        }
        // eslint-disable-next-line eqeqeq
        this.spConfigIndex = currentSettings.sharePoint.sharepointConfig.findIndex((element) => element.key == this.sharePointId);
        this.settings = currentSettings;
      },
      (error) => {
        this.initFailed = true;
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
      }
    );
  }
}
